import React from "react";
import { Card, Row, Col, Typography, Progress, Empty, Tag, Table, Tooltip } from "antd";
import {
  TrophyOutlined,
  ThunderboltOutlined,
  GiftOutlined,
  PercentageOutlined,
  UserOutlined,
  FireOutlined,
} from "@ant-design/icons";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { motion } from "framer-motion";

const { Text, Title } = Typography;

const COLORS = ["#1890ff", "#52c41a", "#722ed1", "#fa8c16", "#13c2c2", "#eb2f96"];

const GamificationStats = ({
  title = "Gamification Performance",
  data = {},
  loading = false,
  style = {},
}) => {
  const {
    totalPlays = 0,
    totalWins = 0,
    totalLosses = 0,
    winRate = 0,
    averageSpinTime = 0,
    prizeDistribution = [],
    recentWinners = [],
    engagementRate = 0,
    repeatPlayRate = 0,
  } = data;

  // Summary stats
  const statsCards = [
    {
      title: "Total Plays",
      value: totalPlays,
      icon: <ThunderboltOutlined />,
      color: "#1890ff",
      backgroundColor: "#e6f7ff",
    },
    {
      title: "Total Wins",
      value: totalWins,
      icon: <TrophyOutlined />,
      color: "#52c41a",
      backgroundColor: "#f6ffed",
    },
    {
      title: "Win Rate",
      value: `${winRate.toFixed(1)}%`,
      icon: <PercentageOutlined />,
      color: "#722ed1",
      backgroundColor: "#f9f0ff",
    },
    {
      title: "Engagement Rate",
      value: `${engagementRate.toFixed(1)}%`,
      icon: <FireOutlined />,
      color: "#fa8c16",
      backgroundColor: "#fff7e6",
    },
  ];

  // Prize distribution pie chart data
  const pieData = prizeDistribution.map((item) => ({
    name: item.prize || item.name,
    value: item.count || item.value,
  }));

  // Recent winners table columns
  const winnersColumns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <UserOutlined style={{ marginRight: 8, color: "#8c8c8c" }} />
          <Text>{user?.email || user?.name || "Anonymous"}</Text>
        </div>
      ),
    },
    {
      title: "Prize",
      dataIndex: "prize",
      key: "prize",
      render: (prize) => (
        <Tag color="gold" icon={<GiftOutlined />}>
          {prize}
        </Tag>
      ),
    },
    {
      title: "Won At",
      dataIndex: "wonAt",
      key: "wonAt",
      render: (date) =>
        new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  // Custom pie chart tooltip
  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Text strong>{payload[0].name}</Text>
        <br />
        <Text type="secondary">Count: {payload[0].value}</Text>
      </div>
    );
  };

  if (!data || (totalPlays === 0 && totalWins === 0)) {
    return (
      <Card title={title} bordered={false} loading={loading} style={style}>
        <Empty
          description="No gamification data available"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={
          <span>
            <TrophyOutlined style={{ marginRight: 8, color: "#faad14" }} />
            {title}
          </span>
        }
        bordered={false}
        loading={loading}
        style={style}
      >
        {/* Summary Stats Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {statsCards.map((stat, index) => (
            <Col xs={12} sm={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Card
                  size="small"
                  style={{
                    background: stat.backgroundColor,
                    borderLeft: `3px solid ${stat.color}`,
                  }}
                  bodyStyle={{ padding: "12px 16px" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      style={{
                        fontSize: 20,
                        color: stat.color,
                        marginRight: 12,
                      }}
                    >
                      {stat.icon}
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {stat.title}
                      </Text>
                      <div style={{ fontSize: 18, fontWeight: 600, color: stat.color }}>
                        {typeof stat.value === "number"
                          ? stat.value.toLocaleString()
                          : stat.value}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        <Row gutter={24}>
          {/* Win/Loss Distribution */}
          <Col xs={24} md={12}>
            <Card
              title="Win/Loss Distribution"
              size="small"
              bordered={false}
              style={{ background: "#fafafa" }}
            >
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Progress
                  type="circle"
                  percent={winRate}
                  width={140}
                  strokeColor={{
                    "0%": "#52c41a",
                    "100%": "#87d068",
                  }}
                  format={(percent) => (
                    <div>
                      <div style={{ fontSize: 24, fontWeight: 600 }}>
                        {percent?.toFixed(1)}%
                      </div>
                      <div style={{ fontSize: 12, color: "#8c8c8c" }}>
                        Win Rate
                      </div>
                    </div>
                  )}
                />
                <Row gutter={16} style={{ marginTop: 20 }}>
                  <Col span={12}>
                    <div
                      style={{
                        padding: 12,
                        background: "#f6ffed",
                        borderRadius: 8,
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Wins
                      </Text>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#52c41a" }}>
                        {totalWins.toLocaleString()}
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div
                      style={{
                        padding: 12,
                        background: "#fff2f0",
                        borderRadius: 8,
                      }}
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Losses
                      </Text>
                      <div style={{ fontSize: 20, fontWeight: 600, color: "#ff4d4f" }}>
                        {totalLosses.toLocaleString()}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>

          {/* Prize Distribution */}
          <Col xs={24} md={12}>
            <Card
              title="Prize Distribution"
              size="small"
              bordered={false}
              style={{ background: "#fafafa" }}
            >
              {pieData.length > 0 ? (
                <div style={{ height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                        labelLine={{ strokeWidth: 1 }}
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<CustomPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <Empty
                  description="No prize data"
                  style={{ padding: "40px 0" }}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Additional Metrics */}
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Tooltip title="Average time users spend on the game before spinning">
              <Card
                size="small"
                style={{ background: "#e6f7ff", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Avg. Engagement Time
                </Text>
                <div style={{ fontSize: 18, fontWeight: 600, color: "#1890ff" }}>
                  {averageSpinTime?.toFixed(1) || 0}s
                </div>
              </Card>
            </Tooltip>
          </Col>
          <Col span={12}>
            <Tooltip title="Percentage of users who played more than once">
              <Card
                size="small"
                style={{ background: "#fff7e6", textAlign: "center" }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Repeat Play Rate
                </Text>
                <div style={{ fontSize: 18, fontWeight: 600, color: "#fa8c16" }}>
                  {repeatPlayRate?.toFixed(1) || 0}%
                </div>
              </Card>
            </Tooltip>
          </Col>
        </Row>

        {/* Recent Winners Table */}
        {recentWinners && recentWinners.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Recent Winners</Title>
            <Table
              columns={winnersColumns}
              dataSource={recentWinners}
              pagination={false}
              size="small"
              rowKey={(record) => record._id || record.id || Math.random()}
            />
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default GamificationStats;
